<template>
  <div class="ai-assistant-page h-100">
    <div class="white-box h-100 d-flex p-0 overflow-hidden">
      <!-- Sidebar for History -->
      <div
        class="chat-sidebar border-right bg-light d-flex flex-column"
        style="width: 260px; border-right: 1px solid #dee2e6"
      >
        <div class="p-3 border-bottom bg-white">
          <button
            class="
              btn btn-primary
              w-100
              d-flex
              align-items-center
              justify-content-center
              gap-2
            "
            @click="startNewChat"
          >
            <BaseIcon name="plus" /> New Chat
          </button>
        </div>
        <div class="flex-grow-1 overflow-auto">
          <div v-if="historyLoading" class="p-3 text-center">
            <span class="spinner-border spinner-border-sm text-primary"></span>
          </div>
          <div
            v-else-if="history.length === 0"
            class="p-3 text-center text-muted small"
          >
            No previous chats
          </div>
          <div v-else class="list-group list-group-flush">
            <button
              v-for="chat in history"
              :key="chat.conversation_id"
              class="list-group-item list-group-item-action border-0 py-3"
              :class="{
                active: currentConversationId === chat.conversation_id,
              }"
              @click="loadConversation(chat.conversation_id)"
            >
              <div class="d-flex align-items-center gap-2">
                <BaseIcon name="messages" class="flex-shrink-0" />
                <div class="text-truncate small fw-medium">
                  {{ chat.first_question || "Untitled Chat" }}
                </div>
              </div>
              <div class="x-small text-muted mt-1 ms-4">
                {{ formatDate(chat.created_at) }}
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Main Chat Area -->
      <div class="flex-grow-1 d-flex flex-column overflow-hidden bg-white">
        <!-- Header -->
        <div
          class="
            p-3
            border-bottom
            d-flex
            justify-content-between
            align-items-center
            bg-white
          "
        >
          <div>
            <h4 class="mb-0 fw-bold">{{ $t("menu.aiAssistant") }}</h4>
            <small class="text-muted">Ask anything about your data</small>
          </div>
          <div class="d-flex gap-2">
            <button
              class="btn btn-outline-secondary btn-sm"
              @click="clearChat"
              v-if="messages.length > 0 && !currentConversationId"
            >
              <BaseIcon name="trash" class="me-1" /> Clear Chat
            </button>
          </div>
        </div>

        <!-- Chat History -->
        <div
          class="
            chat-history
            flex-grow-1
            p-4
            overflow-auto
            d-flex
            flex-column
            gap-3
          "
          ref="chatContainer"
        >
          <div
            v-if="messages.length === 0 && !loading"
            class="empty-state text-center my-auto opacity-50"
          >
            <BaseIcon name="messages" style="font-size: 4rem" />
            <h5 class="mt-3">How can I help you today?</h5>
            <p class="small">
              Try asking: "What were my top selling products last month?"
            </p>
          </div>

          <div
            v-for="(msg, index) in messages"
            :key="index"
            :class="[
              'message-bubble',
              msg.role === 'user'
                ? 'sent align-self-end'
                : 'received align-self-start',
            ]"
          >
            <div class="message-content p-3 rounded-3 shadow-sm">
              <div class="d-flex justify-content-between mb-1 gap-4">
                <small class="fw-bold text-uppercase x-small">{{
                  msg.role === "user" ? "You" : "AI Assistant"
                }}</small>
              </div>
              <div
                class="message-text"
                v-html="formatMessage(msg.content)"
              ></div>

              <!-- SQL & Chart visualization -->
              <div v-if="msg.sql" class="mt-2">
                <details>
                  <summary class="x-small text-muted cursor-pointer">
                    View SQL
                  </summary>
                  <pre
                    class="sql-code mt-1 p-2 bg-dark text-light rounded x-small"
                  ><code>{{ msg.sql }}</code></pre>
                </details>
              </div>
            </div>
          </div>

          <div v-if="loading" class="message-bubble received align-self-start">
            <div class="message-content p-3 rounded-3 shadow-sm">
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Input Area -->
        <div class="p-3 bg-white border-top">
          <div class="input-group">
            <textarea
              class="form-control"
              rows="2"
              :placeholder="$t('messages.typeMessage')"
              v-model="userInput"
              @keydown.enter.prevent="sendMessage"
              :disabled="loading"
            ></textarea>
            <button
              class="btn btn-primary px-4"
              @click="sendMessage"
              :disabled="loading || !userInput.trim()"
            >
              <i class="bi bi-send" v-if="!loading"></i>
              <span class="spinner-border spinner-border-sm" v-else></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/modules/http";
import BaseIcon from "@/components/icons/BaseIcon.vue";
import dayjs from "dayjs";
import calendar from "dayjs/plugin/calendar";
import markdownit from "markdown-it";
const md = markdownit();

dayjs.extend(calendar);

export default {
  name: "AiAssistant",
  components: { BaseIcon },
  data() {
    return {
      messages: [],
      history: [],
      userInput: "",
      loading: false,
      historyLoading: false,
      currentConversationId: null,
    };
  },
  async mounted() {
    await this.fetchHistory();
  },
  methods: {
    formatDate(date) {
      return dayjs(date).calendar();
    },
    async fetchHistory() {
      this.historyLoading = true;
      try {
        const response = await http.fetch("/ai/history");
        this.history = response || [];
      } catch (error) {
        console.error("Failed to fetch AI history:", error);
      } finally {
        this.historyLoading = false;
      }
    },
    async loadConversation(conversationId) {
      if (this.currentConversationId === conversationId) return;

      this.loading = true;
      this.currentConversationId = conversationId;
      this.messages = [];

      try {
        const response = await http.fetch(`/ai/conversation/${conversationId}`);
        if (response && response.messages) {
          this.messages = response.messages.map((m) => ({
            role: m.role,
            content: m.content,
            sql: m.sql,
          }));
        }
        this.$nextTick(() => this.scrollToBottom());
      } catch (error) {
        console.error("Failed to load conversation:", error);
      } finally {
        this.loading = false;
      }
    },
    startNewChat() {
      this.currentConversationId = null;
      this.messages = [];
      this.userInput = "";
    },
    formatMessage(text) {
      if (!text) return "";
      return md.render(text);
    },
    async sendMessage() {
      if (!this.userInput.trim() || this.loading) return;

      const question = this.userInput.trim();
      this.messages.push({ role: "user", content: question });
      this.userInput = "";
      this.loading = true;

      this.$nextTick(() => this.scrollToBottom());

      try {
        const payload = {
          question: question,
          conversation_id: this.currentConversationId,
        };

        const response = await http.fetch("/ai/ask", payload);

        if (response.conversation_id && !this.currentConversationId) {
          this.currentConversationId = response.conversation_id;
          await this.fetchHistory();
        }

        this.messages.push({
          role: "assistant",
          content: response.answer,
          sql: response.sql,
        });
      } catch (error) {
        console.error("AI Assistant Error:", error);
        this.messages.push({
          role: "assistant",
          content:
            "Sorry, I encountered an error while processing your request. Please try again later.",
        });
      } finally {
        this.loading = false;
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    clearChat() {
      this.messages = [];
    },
    scrollToBottom() {
      const container = this.$refs.chatContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
  },
};
</script>

<style scoped>
.chat-sidebar .list-group-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.chat-sidebar .list-group-item:hover {
  background-color: #f1f3f5 !important;
}

.chat-sidebar .list-group-item.active {
  background-color: #e9ecef !important;
  color: inherit;
  border-left: 3px solid #0d6efd !important;
}

.ai-assistant-page {
  height: calc(100vh - 120px);
}

.chat-history {
  background-color: #f8f9fa;
}

.message-bubble {
  max-width: 85%;
}

.message-bubble.received .message-content {
  background-color: #fff;
  border-bottom-left-radius: 2px !important;
}

.message-bubble.sent .message-content {
  background-color: #0d6efd;
  color: white;
  border-bottom-right-radius: 2px !important;
}

.message-bubble.sent .text-muted {
  color: rgba(255, 255, 255, 0.7) !important;
}

.sql-code {
  white-space: pre-wrap;
  word-break: break-all;
}

.x-small {
  font-size: 0.75rem;
}

.cursor-pointer {
  cursor: pointer;
}

.typing-indicator {
  display: flex;
  gap: 4px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background-color: #0d6efd;
  border-radius: 50%;
  display: inline-block;
  animation: bounce 1.3s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}
.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}
</style>
